import * as React from 'react'
import { cn } from '@/lib/utils'
import { HugeiconsIcon } from "@hugeicons/react"
import { InformationCircleIcon, Alert01Icon, AlertCircleIcon } from "@hugeicons/core-free-icons"

/**
 * InlineNotice - Standardized inline notice component
 * 
 * Provides consistent styling for inline messages, warnings, and errors.
 * Use subtle borders and backgrounds - maintains minimalism.
 * 
 * @example
 * ```tsx
 * <InlineNotice variant="info" message="This feature is in beta" />
 * <InlineNotice variant="warning" message="Data may be delayed" />
 * <InlineNotice variant="error" message="Failed to load data" />
 * ```
 */
interface InlineNoticeProps {
  /** Notice variant */
  variant?: 'info' | 'warning' | 'error' | 'success'
  /** Message text */
  message: string
  /** Optional description text */
  description?: string
  /** Custom className */
  className?: string
}

const variantStyles = {
  info: {
    container: 'bg-muted/50 border-border',
    icon: InformationCircleIcon,
    iconColor: 'text-foreground/60',
  },
  warning: {
    container: 'bg-warning-tint border-warning/20',
    icon: Alert01Icon,
    iconColor: 'text-warning',
  },
  error: {
    container: 'bg-destructive/5 border-destructive/20',
    icon: AlertCircleIcon,
    iconColor: 'text-destructive/70',
  },
  success: {
    container: 'bg-success-tint border-success/20',
    icon: InformationCircleIcon,
    iconColor: 'text-success',
  },
}

export function InlineNotice({
  variant = 'info',
  message,
  description,
  className,
}: InlineNoticeProps) {
  const styles = variantStyles[variant]

  return (
    <div
      className={cn(
        'flex items-start gap-3 p-4 rounded-md border',
        styles.container,
        className
      )}
    >
      <HugeiconsIcon icon={styles.icon} size={20} className={cn('shrink-0 mt-0.5', styles.iconColor)} />
      <div className="flex flex-col gap-1 flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground/90">
          {message}
        </p>
        {description && (
          <p className="text-xs text-muted-foreground/70">
            {description}
          </p>
        )}
      </div>
    </div>
  )
}

